/**
 * Generate a square VIP-activation share image on a hidden <canvas>.
 * Returns a Blob (PNG) ready to be passed to navigator.share() or downloaded.
 *
 * Layout (1080×1080):
 *   - Dark gradient backdrop (#050505 → #0a0a0a → Tiffany glow)
 *   - AutoSpa5 logo + wordmark at the top
 *   - Big VIP CARD title
 *   - License plate styled like an actual EU plate (blue band + black text on white)
 *   - "Kam aktivizuar Kartën VIP AutoSpa5!" headline (translated)
 *   - Tiffany sparkle line at the bottom
 */
export async function generateVipShareImage({
  plate = 'AA 000 AA',
  headline = 'Kam aktivizuar Kartën VIP AutoSpa5!',
  brand = '@autospa5',
  logoUrl,
}) {
  const size = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  // Background gradient
  const grad = ctx.createRadialGradient(size / 2, size / 2, 80, size / 2, size / 2, size);
  grad.addColorStop(0, 'rgba(10, 186, 181, 0.32)');
  grad.addColorStop(0.45, '#0a0a0a');
  grad.addColorStop(1, '#050505');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  // Subtle grain dots (Tiffany)
  ctx.fillStyle = 'rgba(10, 186, 181, 0.05)';
  for (let i = 0; i < 90; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = Math.random() * 2 + 0.5;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Outer rounded border
  ctx.strokeStyle = 'rgba(255, 215, 0, 0.45)';
  ctx.lineWidth = 4;
  roundRect(ctx, 40, 40, size - 80, size - 80, 56);
  ctx.stroke();

  // Logo
  if (logoUrl) {
    try {
      const img = await loadImage(logoUrl);
      const lw = 220;
      ctx.drawImage(img, (size - lw) / 2, 110, lw, lw);
    } catch (err) { console.debug('[VIPShare] logo skipped', err?.message); }
  }

  // Wordmark
  ctx.textAlign = 'center';
  ctx.fillStyle = '#FFFFFF';
  ctx.font = 'bold 64px Outfit, sans-serif';
  ctx.fillText('AutoSpa5', size / 2, 380);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.font = '600 22px Outfit, sans-serif';
  ctx.letterSpacing = '6px';
  ctx.fillText('CAR WASH · VIP CARD', size / 2, 420);

  // Crown stars row
  ctx.fillStyle = '#FFD700';
  ctx.font = 'bold 28px Inter, sans-serif';
  ctx.fillText('★ ★ ★ ★ ★', size / 2, 470);

  // License plate (EU style)
  const plateW = 620;
  const plateH = 150;
  const plateX = (size - plateW) / 2;
  const plateY = 530;
  // EU blue strip
  ctx.fillStyle = '#003399';
  roundRect(ctx, plateX, plateY, 60, plateH, 12);
  ctx.fill();
  ctx.fillStyle = '#FFCC00';
  ctx.font = 'bold 26px Inter, sans-serif';
  ctx.fillText('EU', plateX + 30, plateY + plateH / 2 - 10);
  ctx.fillStyle = '#FFFFFF';
  ctx.font = 'bold 22px Inter, sans-serif';
  ctx.fillText('AL', plateX + 30, plateY + plateH / 2 + 20);
  // White plate body
  ctx.fillStyle = '#FFFFFF';
  roundRect(ctx, plateX + 60, plateY, plateW - 60, plateH, 12);
  ctx.fill();
  // Black plate text
  ctx.fillStyle = '#0a0a0a';
  ctx.font = 'bold 92px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(plate.toUpperCase(), plateX + 60 + (plateW - 60) / 2, plateY + plateH / 2 + 34);

  // Tiffany underline
  ctx.fillStyle = '#0ABAB5';
  ctx.fillRect(plateX + 80, plateY + plateH + 14, plateW - 100, 6);

  // Headline (wrap if long)
  ctx.fillStyle = '#0ABAB5';
  ctx.font = 'bold 50px Outfit, sans-serif';
  ctx.textAlign = 'center';
  wrapText(ctx, headline, size / 2, 800, size - 200, 60);

  // Footer brand
  ctx.fillStyle = 'rgba(255, 90, 0, 0.95)';
  ctx.font = 'bold 30px Outfit, sans-serif';
  ctx.fillText(brand, size / 2, size - 80);

  // Convert to Blob
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.95));
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(' ');
  let line = '';
  let yy = y;
  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    if (ctx.measureText(testLine).width > maxWidth && n > 0) {
      ctx.fillText(line.trim(), x, yy);
      line = words[n] + ' ';
      yy += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line.trim(), x, yy);
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}
